import { untrack } from "svelte";

/**
 * Parse `schemaentry` custom-element prop (object or JSON string) without mutating the prop.
 * Mutating props in Svelte 5 (e.g. `schemaentry = JSON.parse(...)`) can sync back to parents and cause update loops.
 */
export function parseSchemaentryProp(se: unknown): Record<string, any> | null {
  if (se == null) return null;
  if (typeof se === "string") {
    try {
      return JSON.parse(se) as Record<string, any>;
    } catch {
      return null;
    }
  }
  if (typeof se === "object") return se as Record<string, any>;
  return null;
}

/**
 * Stable string key for `schemaentry` prop. Use to sync local input state only when the
 * parent-sent schema actually changes — not on every local keystroke (effects that read
 * `value` would otherwise reset the field to stale `field.value`).
 */
export function schemaentryFingerprint(se: unknown): string {
  if (typeof se === "string") return se;
  if (se != null && typeof se === "object") {
    try {
      return JSON.stringify(se);
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * Use inside `$effect` when comparing a new fingerprint to stored `schemaFingerprint`.
 * Reading the stored value via `getStoredFingerprint` inside `untrack()` avoids subscribing
 * the effect to `schemaFingerprint`, so `schemaFingerprint = fp` does not immediately
 * re-enter the same effect and hit `effect_update_depth_exceeded`.
 */
export function shouldApplySchemaSync(
  newFingerprint: string,
  getStoredFingerprint: () => string,
): boolean {
  return untrack(() => newFingerprint !== getStoredFingerprint());
}

/** Compare `setVal` values (primitives by identity; objects/arrays by JSON). */
function setValValuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== "object" || typeof b !== "object") return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/** Compare `setVal` payloads to avoid redundant parent updates. */
export function sameSetValPayload(
  prev: { value: unknown; valid: boolean; id: string | undefined } | null,
  next: { value: unknown; valid: boolean; id: string | undefined },
): boolean {
  if (!prev) return false;
  return (
    prev.valid === next.valid &&
    prev.id === next.id &&
    setValValuesEqual(prev.value, next.value)
  );
}
